import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getApiKeys, createApiKey, deleteApiKey, ApiKey } from '../../../lib/database';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';

const ApiKeysManager: React.FC = () => {
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<ApiKey | null>(null);

  useEffect(() => {
    if (user) {
      fetchApiKeys();
    }
  }, [user]);

  const fetchApiKeys = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const keys = await getApiKeys(user.id);
      setApiKeys(keys);
    } catch (err) {
      setError('Failed to fetch API keys.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newKeyName.trim()) return;

    setIsCreatingKey(true);
    try {
      const rawKey = await createApiKey(newKeyName.trim());
      setNewlyCreatedKey(rawKey);
      await fetchApiKeys(); // Refresh the list
      setNewKeyName('');
    } catch (err) {
      setError('Failed to create API key.');
      console.error(err);
    } finally {
      setIsCreatingKey(false);
    }
  };

  const openDeleteModal = (key: ApiKey) => {
    setKeyToDelete(key);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setKeyToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteKey = async () => {
    if (!keyToDelete) return;

    try {
      await deleteApiKey(keyToDelete.id);
      setApiKeys(apiKeys.filter(key => key.id !== keyToDelete.id));
      closeDeleteModal();
    } catch (err) {
      setError('Failed to delete API key.');
      console.error(err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Add some user feedback, e.g., a toast notification
  };

  if (isLoading) {
    return <div className="text-center p-6">Loading API Keys...</div>;
  }

  if (error) {
    return <div className="text-center p-6 text-red-500">{error}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-2xl font-semibold text-gray-800">API Keys</h3>
        <p className="text-gray-500 mt-1">Manage your API keys for programmatic access.</p>
      </div>

      {newlyCreatedKey && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800">New API Key Created</h4>
          <p className="text-green-700 mt-1">Please copy your new API key. You won't be able to see it again.</p>
          <div className="mt-2 bg-green-100 p-2 rounded flex items-center justify-between">
            <code className="text-sm text-green-900">{newlyCreatedKey}</code>
            <button onClick={() => copyToClipboard(newlyCreatedKey)} className="text-sm font-medium text-green-800 hover:underline">Copy</button>
          </div>
          <button onClick={() => setNewlyCreatedKey(null)} className="mt-2 text-sm font-medium text-green-800 hover:underline">Close</button>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h4 className="text-lg font-medium text-gray-900 mb-2">Create New API Key</h4>
        <form onSubmit={handleCreateKey} className="flex items-center space-x-4">
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Enter a name for the key"
            className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-netlify-teal focus:border-netlify-teal"
            disabled={isCreatingKey}
          />
          <button
            type="submit"
            disabled={!newKeyName.trim() || isCreatingKey}
            className="px-4 py-2 bg-netlify-teal text-white rounded-lg font-semibold hover:bg-netlify-600 transition-colors disabled:bg-gray-400 flex items-center justify-center w-36"
          >
            {isCreatingKey ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Create Key'
            )}
          </button>
        </form>
      </div>

      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Your Keys</h4>
        <div className="space-y-4">
          {apiKeys.length === 0 ? (
            <p className="text-gray-500">You don't have any API keys yet.</p>
          ) : (
            apiKeys.map(key => (
              <div key={key.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                <div>
                  <p className="font-semibold text-gray-800">{key.description}</p>
                  <p className="text-sm text-gray-500">
                    Key Prefix: <code className="bg-gray-100 p-1 rounded text-xs">{key.key_prefix}...</code>
                  </p>
                  <p className="text-sm text-gray-500">
                    Created on {new Date(key.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => openDeleteModal(key)}
                  className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                >
                  Delete
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteKey}
        title="Delete API Key"
        description="Are you sure you want to delete this API key? This action is irreversible."
        confirmText="Delete"
      />
    </div>
  );
};

export default ApiKeysManager;
